import { useEffect, useState } from "react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { supabase } from "@/lib/supabase"
import {
  PERMISSION_KEYS,
  PERMISSION_LABELS,
  type PermissionKey,
} from "@/lib/permissions"
import type { Json } from "@/types/database"

interface PermissionEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  memberId: string
  memberLabel: string
  role: string
  permissions: Json | null
  onSaved: () => void
}

export function PermissionEditorModal({
  open,
  onOpenChange,
  memberId,
  memberLabel,
  role,
  permissions,
  onSaved,
}: PermissionEditorModalProps) {
  const parsePerm = (v: unknown): boolean => v === true || v === "true"
  const [localPerms, setLocalPerms] = useState<Record<PermissionKey, boolean>>(
    () => {
      const init: Record<string, boolean> = {}
      const raw = (permissions as Record<string, unknown> | null) ?? {}
      for (const key of PERMISSION_KEYS) {
        init[key] = parsePerm(raw[key])
      }
      return init as Record<PermissionKey, boolean>
    }
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    const raw = (permissions as Record<string, unknown> | null) ?? {}
    const next: Record<string, boolean> = {}
    for (const key of PERMISSION_KEYS) {
      next[key] = parsePerm(raw[key])
    }
    setLocalPerms(next as Record<PermissionKey, boolean>)
  }, [open, permissions])

  const handleToggle = (key: PermissionKey, value: boolean) => {
    setLocalPerms((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    setSaving(true)
    const permObj: Record<string, boolean> = {}
    for (const key of PERMISSION_KEYS) {
      permObj[key] = localPerms[key] ?? false
    }
    const { error } = await supabase
      .from("organization_members")
      .update({ permissions: permObj })
      .eq("id", memberId)
    setSaving(false)
    if (error) {
      toast.error("Failed to save permissions")
      console.error(error)
      return
    }
    toast.success("Permissions updated")
    onSaved()
    onOpenChange(false)
  }

  const isOwner = role === "owner"
  if (isOwner) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permissions</DialogTitle>
            <DialogDescription>
              Owners have all permissions and cannot be restricted.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {memberLabel} is an owner and has full access to this organization.
          </p>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit permissions</DialogTitle>
          <DialogDescription>
            Customize permissions for {memberLabel}. Unchecked permissions use the role default.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 py-4">
          {PERMISSION_KEYS.map((key) => (
            <label
              key={key}
              className="flex items-center gap-3 rounded-md border px-3 py-2 hover:bg-muted/50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={localPerms[key] ?? false}
                onChange={(e) => handleToggle(key, e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <span className="text-sm font-medium">{PERMISSION_LABELS[key]}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
